import React, { useEffect, useMemo } from 'react';

import { Route, Routes, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import { useUserDetails } from 'api/useUserDetails';
import { Project } from 'common/contracts';
import { ELocalStorageKeys } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { localStorageUtil } from 'utils/localStorageUtil';

import {
  bundleDesigns,
  bundleDesignsByTheme
} from 'components/TemplatesStudio/DesignsMetadata/bundles.metadata';
import {
  promotionDesigns,
  promotionDesignsByTheme
} from 'components/TemplatesStudio/DesignsMetadata/promotions.metadata';
import {
  rollingOffersDesigns,
  rollingOffersDesignsByTheme
} from 'components/TemplatesStudio/DesignsMetadata/rollingOffers.metadata';

import {
  dailyBonusBadgesDesigns,
  dailyBonusBadgesDesignsByTheme
} from '../DesignsMetadata/dailyBonusBadges.metadata';
import {
  popupDesigns,
  popupDesignsByTheme
} from '../DesignsMetadata/popups.metadata';
import { progressBarsDesigns } from '../DesignsMetadata/progressBars.metadata';
import { triggeredPopupsDesigns } from '../DesignsMetadata/triggeredPopups.metadata';
import { triggeredPopupsBadgesDesigns } from '../DesignsMetadata/triggeredPopupsBadges.metadata';
import { EThemes } from '../enums';
import { getDefaultBodyBlocksOrder, isDesignNew } from '../utils';

import { TemplateStudioForm } from './FormPage/Form';
import { TemplatesStudioMainPage } from './MainPage/Main';

const formatDateMMYY = (dateString: string): string => {
  const date = new Date(dateString);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = String(date.getFullYear()).slice(-2);
  return `${month}-${year}`;
};

export const TemplatesStudioView: React.FC = () => {
  const {
    setCurrentCompany,
    setDefaultBodyBlocksOrder,
    setSupportedThemes,
    setSupportedThemesForBundles,
    setSupportedThemesForPromotions,
    setSupportedThemesForRollingOffers,
    setSupportedThemesForDailyBonus,
    setRelevantBundleDesigns,
    setRelevantPromotionDesigns,
    setRelevantRollingOffersDesigns,
    setRelevantDailyBonusBadgesDesigns,
    setRelevantProgressBarDesigns,
    setRelevantPopupsDesigns,
    setRelevantTriggeredPopupsDesigns,
    setRelevantTriggeredPopupsBadgeDesigns,
    supportedThemes
  } = useTemplateStudioState();
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { userDetails } = useUserDetails();

  useEffect(() => {
    if (localStorageUtil.get(ELocalStorageKeys.PLAYER_DATA)) return;

    localStorageUtil.set(ELocalStorageKeys.PLAYER_DATA, {
      playerId: '123456',
      playerName: 'Player Name'
    });
  }, []);

  const projectThemes = useMemo(
    () =>
      userDetails?.projects?.find(
        (project: Project) => project.publisherId === publisherId
      )?.themes || [EThemes.ROYAL_BLAST],
    [userDetails?.projects]
  );

  useEffect(() => {
    setSupportedThemes(projectThemes);
    setSupportedThemesForBundles(
      projectThemes.filter(
        (theme) => (bundleDesignsByTheme[theme]?.length ?? 0) > 0
      )
    );
    setSupportedThemesForPromotions(
      projectThemes.filter(
        (theme) => (promotionDesignsByTheme[theme]?.length ?? 0) > 0
      )
    );
    setSupportedThemesForRollingOffers(
      projectThemes.filter(
        (theme) => (rollingOffersDesignsByTheme[theme]?.length ?? 0) > 0
      )
    );
    setSupportedThemesForDailyBonus(
      projectThemes.filter(
        (theme) =>
          (dailyBonusBadgesDesignsByTheme[theme]?.length ?? 0) > 0 ||
          (popupDesignsByTheme[theme]?.length ?? 0) > 0
      )
    );

    return () => {
      setSupportedThemes([]);
      setSupportedThemesForBundles([]);
      setSupportedThemesForPromotions([]);
      setSupportedThemesForRollingOffers([]);
      setSupportedThemesForDailyBonus([]);
    };
  }, [projectThemes.length]);

  useEffect(() => {
    if (!supportedThemes || supportedThemes.length === 0) return;
    const relevantBundleDesigns = Object.values(bundleDesigns).filter(
      ({ theme }) => supportedThemes!.includes(theme)
    );

    setRelevantBundleDesigns(
      relevantBundleDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantPromotionDesigns = Object.values(promotionDesigns).filter(
      ({ theme }) => supportedThemes!.includes(theme)
    );

    setRelevantPromotionDesigns(
      relevantPromotionDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantRollingOffersDesigns = Object.values(
      rollingOffersDesigns
    ).filter(({ theme }) => supportedThemes!.includes(theme));

    setRelevantRollingOffersDesigns(
      relevantRollingOffersDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantDailyBonusBadgesDesigns = Object.values(
      dailyBonusBadgesDesigns
    ).filter(({ theme }) => supportedThemes!.includes(theme));

    setRelevantDailyBonusBadgesDesigns(
      relevantDailyBonusBadgesDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantProgressBarDesigns = Object.values(
      progressBarsDesigns
    ).filter(({ theme }) => supportedThemes!.includes(theme));

    setRelevantProgressBarDesigns(
      relevantProgressBarDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantPopupsDesigns = Object.values(popupDesigns).filter(
      ({ theme }) => supportedThemes!.includes(theme)
    );

    setRelevantPopupsDesigns(
      relevantPopupsDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantTriggeredPopupsDesigns = Object.values(
      triggeredPopupsDesigns
    ).filter(({ theme }) => supportedThemes!.includes(theme));

    setRelevantTriggeredPopupsDesigns(
      relevantTriggeredPopupsDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );

    const relevantTriggeredPopupsBadgeDesigns = Object.values(
      triggeredPopupsBadgesDesigns
    ).filter(({ theme }) => supportedThemes!.includes(theme));

    setRelevantTriggeredPopupsBadgeDesigns(
      relevantTriggeredPopupsBadgeDesigns.map((design) => {
        const isNew = isDesignNew(design.createdAt);
        return {
          ...design,
          isNew,
          name: isNew
            ? `${design.name} (${formatDateMMYY(design.createdAt)})`
            : design.name
        };
      })
    );
  }, [supportedThemes?.length]);

  useEffect(() => {
    if (userDetails?.company) {
      setCurrentCompany(userDetails.company);
    }

    return () => {
      setCurrentCompany(undefined);
    };
  }, []);

  useEffect(() => {
    if (featureFlags) {
      setDefaultBodyBlocksOrder(getDefaultBodyBlocksOrder(featureFlags));
    }
  }, [featureFlags]);

  return (
    <Routes>
      <Route path="/" element={<TemplatesStudioMainPage />} />
      <Route path="/form" element={<TemplateStudioForm />} />
      <Route
        path="/form/:pageId"
        element={<TemplateStudioForm edit={true} />}
      />
    </Routes>
  );
};
